/**
 * /picks/u/:id — somebody else's card with yours laid over it.
 *
 * Each game is one row with the two teams side by side. Their pick is the
 * filled side; yours is marked "(me)" on whichever side you took. When you both
 * took the same team the row carries one filled side with "(me)" on it, which
 * is the quickest possible answer to the only question anyone is asking, which
 * is "did we disagree".
 *
 * Exactly one thing on this screen is highlighted, and it is theirs. Your own
 * pick used to carry a `bright` outline as well, which put two emphasised
 * sides in a row that is about one person's card — you already know what you
 * picked, and the outline competed with the answer you came for. Your side now
 * looks like any other side and simply says "(me)".
 *
 * D84 is intact: a game that has not kicked off shows their side as hidden. You
 * can see your own pick there, because it is yours.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { PicksCompare as Compare, PicksCompareRow } from '@shared/types'
import { teamAbbr } from '@shared/teams'
import { ApiFailure, GET } from '../lib/api'
import { Avatar, Body, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'

const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'short' })
const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

function kickoff(at: number): string {
  return `${WEEKDAY.format(at)} ${HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')}`
}

function Side({
  teamId, row, side,
}: { teamId: string; row: PicksCompareRow; side: 'home' | 'away' }) {
  const theirs = row.theirPick === teamId
  const mine = row.myPick === teamId
  const correct = theirs ? row.theirCorrect : null

  return (
    <div
      style={{
        flex: 1, minWidth: 0, textAlign: 'center',
        padding: '12px 8px', borderRadius: 12,
        background: theirs ? 'var(--fill)' : 'transparent',
        color: theirs ? 'var(--ink)' : 'var(--fg)',
        border: theirs ? '1px solid var(--fill)' : '1px solid var(--line)',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: theirs ? 700 : 600 }}>
        {teamAbbr(teamId)}
        {correct === true ? ' ✓' : correct === false ? ' ✗' : ''}
      </div>
      {mine ? (
        <div
          className="mono"
          style={{
            fontSize: 10, marginTop: 3,
            // `dim` is illegal on a `fill` ground; on their pick we're on fill.
            color: theirs ? 'var(--ink)' : 'var(--dim)',
            opacity: theirs ? 0.85 : 1,
          }}
        >
          (me)
        </div>
      ) : null}
      {/* Keeps both sides the same height whether or not either is marked. */}
      {!mine ? <div style={{ height: 13, marginTop: 3 }} aria-hidden /> : null}
      <div style={{ fontSize: 11, marginTop: 2, opacity: theirs ? 0.85 : 0.75 }}>
        {side === 'away' ? 'away' : 'home'}
      </div>
    </div>
  )
}

export default function PicksCompare() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const weekParam = params.get('week')

  const [data, setData] = useState<Compare | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const ac = new AbortController()
    setData(null)
    void (async () => {
      try {
        const qs = weekParam ? `?week=${encodeURIComponent(weekParam)}` : ''
        setData(await GET<Compare>(`/api/picks/u/${encodeURIComponent(id)}${qs}`, ac.signal))
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load those picks.')
      }
    })()
    return () => ac.abort()
  }, [id, weekParam])

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        logo={false}
        wordmark={false}
        left={
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{ fontSize: 15, fontWeight: 700, minWidth: 44, minHeight: 44, textAlign: 'left' }}
          >
            ←
          </button>
        }
        title={
          <span className="screen-title">
            {data ? data.user.displayName : 'Picks'}
          </span>
        }
        right={
          data ? (
            <span className="mono" style={{ fontSize: 12, color: 'var(--dim)' }}>
              {data.points} pts
            </span>
          ) : undefined
        }
      />

      <Body style={{ padding: '6px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!data ? (
          <><SkeletonCard lines={3} /><Spacer h={12} /><SkeletonCard lines={4} /></>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar initials={data.user.initials} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {data.made} of {data.total} picked
                </div>
                <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2 }}>
                  {data.weekLabel} · your picks are marked (me)
                </div>
              </div>
              <Link
                to={`/u/${encodeURIComponent(data.user.id)}`}
                style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, flex: '0 0 auto' }}
              >
                Profile →
              </Link>
            </div>

            <Spacer h={16} />

            {data.rows.length === 0 ? (
              <div style={{ fontSize: 15, fontWeight: 600 }}>No games this week.</div>
            ) : (
              data.rows.map((r) => (
                <div key={r.game.id} style={{ marginTop: 14 }}>
                  <div
                    className="mono"
                    style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--dim)' }}
                  >
                    <span>{kickoff(r.game.kickoffAt)}</span>
                    <span>
                      {r.game.state === 'post'
                        ? `${teamAbbr(r.game.awayTeamId)} ${r.game.awayScore ?? 0} · ${teamAbbr(r.game.homeTeamId)} ${r.game.homeScore ?? 0}`
                        : r.hidden ? 'their pick is hidden until kickoff' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <Side teamId={r.game.awayTeamId} row={r} side="away" />
                    <Side teamId={r.game.homeTeamId} row={r} side="home" />
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </Body>
    </Screen>
  )
}
